import { useMemo } from 'react';
import { useInventoryMovements } from '../api/inventory';
import type { InventoryStateRow } from '../api/inventory';
import { Modal } from '../components/Modal';
import { consolidateMovements } from '../lib/inventoryJournal';

export interface InventoryHistoryDrawerProps {
  eventId: string;
  /**
   * Minimal product context for the drawer header. Either an InventoryStateRow
   * (rich, from the inventory state report) or a lighter shape used by
   * SalesByProduct which has fewer fields available.
   */
  product: Pick<InventoryStateRow, 'productId' | 'name'>;
  onClose: () => void;
}

const ADMIN_REASON_LABEL: Record<string, string> = {
  admin_release_transaction: 'Released from transaction',
  admin_restock: 'Restock',
  admin_adjust: 'Adjust',
  admin_set_starting: 'Set starting',
  tracking_enabled: 'Tracking enabled',
  tracking_disabled: 'Tracking disabled',
};

function formatTimeRange(firstAt: string, lastAt: string): string {
  const a = new Date(firstAt);
  const b = new Date(lastAt);
  if (a.getTime() === b.getTime()) return a.toLocaleString();
  // Same calendar day: show date once and a time-range. Different days: full both.
  const sameDay =
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay) {
    return `${a.toLocaleDateString()} ${a.toLocaleTimeString()} → ${b.toLocaleTimeString()}`;
  }
  return `${a.toLocaleString()} → ${b.toLocaleString()}`;
}

export function InventoryHistoryDrawer({
  eventId,
  product,
  onClose,
}: InventoryHistoryDrawerProps) {
  const { data, isLoading } = useInventoryMovements(eventId, product.productId);
  const journal = useMemo(() => (data ? consolidateMovements(data) : []), [data]);

  return (
    <Modal title="History" sub={product.name} width={620} onClose={onClose}>
      {isLoading && <div style={{ color: 'var(--fg-3)' }}>Loading…</div>}
      {data && data.length === 0 && (
        <div style={{ color: 'var(--fg-3)' }}>No movements yet.</div>
      )}
      {data && data.length > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th>When</th>
              <th>Entry</th>
              <th className="num">Δ</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {journal.map((entry) => {
              if (entry.kind === 'sale_run') {
                return (
                  <tr key={entry.key}>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                      {formatTimeRange(entry.firstAt, entry.lastAt)}
                    </td>
                    <td>
                      {entry.count} {entry.count === 1 ? 'sale' : 'sales'}
                    </td>
                    <td className="num" style={{ color: 'var(--oeg-danger)' }}>
                      {entry.totalDelta > 0 ? `+${entry.totalDelta}` : entry.totalDelta}
                    </td>
                    <td style={{ color: 'var(--fg-4)' }}></td>
                  </tr>
                );
              }
              const m = entry.movement;
              return (
                <tr key={m.id}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(m.createdAt).toLocaleString()}
                  </td>
                  <td>{ADMIN_REASON_LABEL[m.reason] ?? m.reason}</td>
                  <td
                    className="num"
                    style={{
                      color: m.delta < 0 ? 'var(--oeg-danger)' : 'var(--oeg-success)',
                    }}
                  >
                    {m.delta > 0 ? `+${m.delta}` : m.delta}
                  </td>
                  <td>{m.note ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
